import React, { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';
import { X, Plus, Tag, Info, Loader2 } from 'lucide-react';

interface ManualTag {
  id: number;
  tag: string;
  color: string;
}

interface AutoTag {
  key: string;
  label: string;
  color: string;
  rule: string;
}

interface TagManagerProps {
  phone: string;
  name: string;
  orderCount: number;
  totalSpent: number;
  manualTags: ManualTag[];
  onTagsChange: () => void;
}

const TAG_COLORS: Record<string, string> = {
  gray: 'bg-gray-100 text-gray-700 border-gray-200',
  red: 'bg-red-100 text-red-700 border-red-200',
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  teal: 'bg-teal-100 text-teal-700 border-teal-200',
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  pink: 'bg-pink-100 text-pink-700 border-pink-200',
};

const COLOR_OPTIONS = [
  { key: 'gray', label: '灰' },
  { key: 'red', label: '紅' },
  { key: 'orange', label: '橙' },
  { key: 'yellow', label: '黃' },
  { key: 'green', label: '綠' },
  { key: 'teal', label: '青' },
  { key: 'blue', label: '藍' },
  { key: 'purple', label: '紫' },
  { key: 'pink', label: '粉' },
];

const QUICK_TAGS = ['重點跟進', '企業客戶', '長期訂閱', '問題客戶', '地址不清'];

const TagManager: React.FC<TagManagerProps> = ({
  phone,
  name,
  orderCount,
  totalSpent,
  manualTags,
  onTagsChange,
}) => {
  const [newTag, setNewTag] = useState('');
  const [selectedColor, setSelectedColor] = useState('blue');
  const [adding, setAdding] = useState(false);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [displayManualTags, setDisplayManualTags] = useState<ManualTag[]>(manualTags);
  const [displayAllTags, setDisplayAllTags] = useState<string[]>([]);

  useEffect(() => {
    setDisplayManualTags(manualTags);
  }, [manualTags]);

  useEffect(() => {
    setDisplayAllTags(allTags);
  }, [allTags]);

  const getAutoTags = (): AutoTag[] => {
    const tags: AutoTag[] = [];
    if (orderCount === 1) {
      tags.push({ key: 'new-customer', label: '新客戶', color: 'bg-blue-100 text-blue-700 border-blue-200', rule: '首次下單（1筆）' });
    } else if (orderCount >= 2 && orderCount < 5) {
      tags.push({ key: 'returning', label: '回頭客', color: 'bg-purple-100 text-purple-700 border-purple-200', rule: '2–4筆訂單' });
    }
    if (orderCount >= 5) {
      tags.push({ key: 'vip', label: 'VIP', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', rule: '累計 ≥5 筆' });
    }
    if (totalSpent >= 500) {
      tags.push({ key: 'big-order', label: '大單', color: 'bg-amber-100 text-amber-700 border-amber-200', rule: '單筆 ≥$500' });
    }
    return tags;
  };

  const isAssigned = (tag: string) => displayManualTags.some(t => t.tag === tag);

  const optimisticallyAddTag = (tag: string) => {
    if (isAssigned(tag)) return false;
    setDisplayManualTags(prev => [...prev, { id: -Date.now(), tag, color: selectedColor }]);
    setDisplayAllTags(prev => prev.filter(t => t !== tag));
    return true;
  };

  const handleAddTag = async () => {
    const tag = newTag.trim();
    if (!tag) return;
    if (isAssigned(tag)) {
      alert('此客戶已經有這個標籤');
      return;
    }
    optimisticallyAddTag(tag);
    setAdding(true);
    const res = await apiFetch(`/api/public/admin/customers/${phone}/tags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tag, color: selectedColor }),
    });
    setAdding(false);
    if (res.ok) {
      setNewTag('');
      onTagsChange();
    } else {
      alert('添加標籤失敗');
      onTagsChange();
    }
  };

  const handleDeleteTag = async (id: number) => {
    if (!confirm('確定刪除此標籤？')) return;
    const tagToDelete = displayManualTags.find(t => t.id === id);
    if (tagToDelete) {
      setDisplayManualTags(prev => prev.filter(t => t.id !== id));
      setDisplayAllTags(prev => (prev.includes(tagToDelete.tag) ? prev : [...prev, tagToDelete.tag]));
    }
    const res = await apiFetch(`/api/public/admin/customers/${phone}/tags/${id}`, {
      method: 'DELETE',
    });
    if (res.ok) {
      onTagsChange();
    } else {
      alert('刪除失敗');
      onTagsChange();
    }
  };

  const handleQuickTag = async (tag: string) => {
    if (isAssigned(tag)) return;
    optimisticallyAddTag(tag);
    setAdding(true);
    const res = await apiFetch(`/api/public/admin/customers/${phone}/tags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tag, color: selectedColor }),
    });
    setAdding(false);
    if (res.ok) {
      onTagsChange();
    } else {
      alert('添加標籤失敗');
      onTagsChange();
    }
  };

  useEffect(() => {
    let mounted = true;
    const fetchTags = async () => {
      setLoadingTags(true);
      try {
        const res = await apiFetch('/api/public/admin/customer-tags/all');
        if (res.ok && mounted) {
          setAllTags(await res.json());
        }
      } catch (e) {
        console.error('Failed to fetch all tags:', e);
      } finally {
        if (mounted) setLoadingTags(false);
      }
    };
    fetchTags();
    return () => { mounted = false; };
  }, []);

  const autoTags = getAutoTags();
  const assignedTagSet = new Set(displayManualTags.map(t => t.tag));
  const existingTags = displayAllTags.filter(tag => !assignedTagSet.has(tag));

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-2 pb-3 border-b border-gray-200 dark:border-gray-700">
        <Tag size={18} className="text-orange-600" />
        <h3 className="font-bold text-gray-800 dark:text-white">標籤管理 — {name}</h3>
        <span className="text-xs text-gray-400">{phone}</span>
      </div>

      {/* Auto Tags */}
      <div>
        <div className="flex items-center gap-1 mb-2">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">系統自動標籤</span>
          <Info size={12} className="text-gray-400" title="根據訂單數據自動計算，不可手動修改" />
        </div>
        {autoTags.length === 0 ? (
          <p className="text-xs text-gray-400 italic">暫無符合條件的自動標籤</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {autoTags.map(t => (
              <div key={t.key} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium border ${t.color}`}>
                <span>{t.label}</span>
                <span className="opacity-60 text-[10px]">({t.rule})</span>
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 text-[10px] text-gray-400 bg-gray-50 dark:bg-gray-900 p-2 rounded border border-gray-100 dark:border-gray-700">
          <strong>自動標籤規則：</strong>新客戶(1筆) / 回頭客(2–4筆) / VIP(≥5筆) / 大單(≥$500)
        </div>
      </div>

      {/* Manual Tags */}
      <div>
        <div className="flex items-center gap-1 mb-2">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">手動標籤</span>
          <span className="text-[10px] text-gray-400">（管理員可自由添加/刪除）</span>
        </div>
        {displayManualTags.length === 0 ? (
          <p className="text-xs text-gray-400 italic mb-2">暫無手動標籤</p>
        ) : (
          <div className="flex flex-wrap gap-2 mb-3">
            {displayManualTags.map(t => (
              <span
                key={t.id}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium border ${TAG_COLORS[t.color] || TAG_COLORS.gray}`}
              >
                {t.tag}
                <button
                  onClick={() => handleDeleteTag(t.id)}
                  className="hover:opacity-70 ml-0.5"
                  title="刪除"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Existing tags pool */}
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-1 mb-2">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">選擇現有標籤</span>
            <span className="text-[10px] text-gray-400">（點擊即可加入此客戶）</span>
          </div>
          {loadingTags ? (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Loader2 className="w-3 h-3 animate-spin" /> 載入中...
            </div>
          ) : existingTags.length === 0 ? (
            <p className="text-xs text-gray-400 italic">暫無其他可選標籤</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {existingTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => handleQuickTag(tag)}
                  disabled={adding}
                  className="text-xs px-2 py-1 rounded border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-orange-50 hover:border-orange-200 dark:hover:bg-orange-900/20 dark:hover:border-orange-700 disabled:opacity-40 transition"
                >
                  + {tag}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Add new tag */}
        <div className="flex items-center gap-2 mb-2">
          <input
            type="text"
            value={newTag}
            onChange={e => setNewTag(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAddTag(); }}
            placeholder="輸入新標籤名稱..."
            className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
            disabled={adding}
          />
          <select
            value={selectedColor}
            onChange={e => setSelectedColor(e.target.value)}
            className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white"
            disabled={adding}
          >
            {COLOR_OPTIONS.map(c => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
          <button
            onClick={handleAddTag}
            disabled={adding || !newTag.trim()}
            className="bg-orange-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-orange-700 disabled:opacity-40 flex items-center gap-1"
          >
            <Plus size={14} /> {adding ? '...' : '添加'}
          </button>
        </div>

        {/* Quick tags */}
        <div className="flex flex-wrap gap-1.5">
          <span className="text-[10px] text-gray-400 mr-1">快捷：</span>
          {QUICK_TAGS.map(tag => (
            <button
              key={tag}
              onClick={() => handleQuickTag(tag)}
              disabled={adding || displayManualTags.some(t => t.tag === tag)}
              className="text-[10px] px-2 py-0.5 rounded border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-orange-50 hover:border-orange-200 disabled:opacity-30 disabled:cursor-not-allowed transition"
            >
              + {tag}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TagManager;
